import DataGridTable from "../shared/tables/DataGridTable";
import SectionContainer from "../shared/containers/SectionContainer";
import { applicationPaymentsDetailsListDataGridColumns } from "./fieldsLists";
import { ArrowBack, Download, MonetizationOn } from "@mui/icons-material";
import {
  Box,
  Button,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
  } from "@mui/material";
import { calculatePaymentforParticipant } from "../../services/paymentsService";
import { ContestWithId } from "../../interfaces/IContests";
import { findParticipantById } from "../../utils/firestore";
import { GridColDef } from "@mui/x-data-grid";
import { useApplicationContext } from "../../contexts/ApplicationContext";
import { useContestContext } from "../../contexts/ContestContext";
import { useEffect, useMemo, useState } from "react";
import { useNavigationHelper } from "../../hooks/useNavigationHelper";
import { useUserContext } from "../../contexts/UserContext";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

interface RowType {
  id: string;
  firstName: string;
  lastName: string;
  numberOfAppearances: number;
  paymentAmount: number;
}

export default function ApplicationPaymentsDetails() {
  const { user } = useUserContext();
  const { application } = useApplicationContext();
  const { contest } = useContestContext();
  const { goToApplicationDetails } = useNavigationHelper();
  const [rows, setRows] = useState<RowType[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  useEffect(() => {
    if (application && contest) {
      const participantIds = Object.keys(application.participantPresentationsMap); // Pobieramy ID zawodników
      const participantPresentationsMap = new Map(
        Object.entries(application.participantPresentationsMap)
      );

      const fetchParticipantsData = async () => {
        const participantsData = await Promise.all(
          participantIds.map(async (participantId) => {
            const numberOfAppearances = participantPresentationsMap.get(participantId) || 0;
            const participant = await findParticipantById(user?.id as string, participantId);
            if (participant) {
              return {
                id: participantId,
                firstName: participant.firstName,
                lastName: participant.lastName,
                numberOfAppearances,
                paymentAmount: calculatePaymentforParticipant(
                  contest as ContestWithId,
                  numberOfAppearances
                ),
              };
            }
            return null;
          })
        );
        // Filtrowanie wyników, aby usunąć null (uczestników, którzy nie zostali znalezieni)
        const typedRows = participantsData.filter((row): row is RowType => row !== null);
        setRows(typedRows);
      };

      fetchParticipantsData();
    }
    //eslint-disable-next-line
  }, [application, contest]);

  // Sortowanie alfabetyczne: nazwisko A-Z, imię A-Z
  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const ln = a.lastName.localeCompare(b.lastName, "pl", { sensitivity: "base" });
      if (ln !== 0) return ln;
      return a.firstName.localeCompare(b.firstName, "pl", { sensitivity: "base" });
    });
    return copy;
  }, [rows]);

  // Filtrowanie po imieniu/nazwisku
  const filteredRows = useMemo(() => {
    if (!search) return sortedRows;
    const s = search.toLowerCase();
    return sortedRows.filter(
      (r) => r.firstName.toLowerCase().includes(s) || r.lastName.toLowerCase().includes(s)
    );
  }, [sortedRows, search]);

  // Paginacja na froncie
  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const totalCount = filteredRows.length;

  const handleExportCsv = () => {
    const data = filteredRows;
    const header = ["Id", "Imię", "Nazwisko", "Liczba wystąpień", "Opłata startowa [PLN]"];
    const lines = [
      header.join(";"),
      ...data.map((r) =>
        [r.id, r.firstName, r.lastName, r.numberOfAppearances, r.paymentAmount]
          .map((v) => `${v}`.replace(/;/g, ","))
          .join(";")
      ),
    ];
    const csvContent = "\uFEFF" + lines.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const fileName = `platnosci_${application?.id || "aplikacja"}.csv`;
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <SectionContainer title="Szczegóły płatności">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Header: tytuł + powrót w jednej linii */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <MonetizationOn sx={{ color: "#6366f1" }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#2f2f2f" }}>
              Opłaty: {contest?.name || "-"}
            </Typography>
          </Stack>

          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBack />}
            onClick={() =>
              application &&
              goToApplicationDetails(application.contestId as string, application.id as string)
            }
            sx={{ textTransform: "none" }}
          >
            Wróć do zgłoszenia
          </Button>
        </Stack>

        {/* Pasek narzędzi: wyszukiwarka + eksport */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <TextField
            size="small"
            placeholder="Szukaj zawodnika (imię, nazwisko)..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ maxWidth: 360 }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={handleExportCsv}
            sx={{ textTransform: "none" }}
          >
            Eksport do Excel (CSV)
          </Button>
        </Stack>

        {/* Prosta tabela 3-kolumnowa */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderColor: "#ddd" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Zawodnik</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 160 }}>Wystąpienia</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 200 }}>Kwota [PLN]</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{`${r.firstName} ${r.lastName}`}</TableCell>
                  <TableCell>{r.numberOfAppearances}</TableCell>
                  <TableCell>{r.paymentAmount}</TableCell>
                </TableRow>
              ))}
              {pagedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>Brak wyników</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginacja (front) */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Typography variant="body2" sx={{ color: "#666" }}>
            Wyświetlono {Math.min((page + 1) * rowsPerPage, totalCount)} z {totalCount}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ color: "#666" }}>
              Wierszy na stronę:
            </Typography>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
            >
              {[15, 25, 50, 100].map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Poprzednia
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPage((p) => ((p + 1) * rowsPerPage < totalCount ? p + 1 : p))}
              disabled={(page + 1) * rowsPerPage >= totalCount}
            >
              Następna
            </Button>
          </Stack>
        </Stack>
      </Box>
    </SectionContainer>
  );
}
